import { FilePreview } from '@components'
import { ExpenseClaimExpense } from '@query'
import { formatEurosWithOpts } from '@utils/formatCurrency'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { ExpenseClaimReceiptPreview } from './ExpenseClaimReceiptPreview'
import { Card, CardTitle, Help } from './shared'

type Props = {
  slug: string
  claimId: number
  expenses: ExpenseClaimExpense[]
}

export const ClaimSummaryAttachments: React.FC<Props> = ({ slug, claimId, expenses }) => {
  const [t] = useTranslation()
  const withReceipt = expenses.filter(expense => expense.attachment?.id)

  if (withReceipt.length === 0) return null

  return (
    <Card>
      <CardTitle>
        {t('travelAndExpenses.summary.attachments', { count: withReceipt.length })}
      </CardTitle>
      <Grid>
        {withReceipt.map(expense => (
          <Tile key={expense.id}>
            <FilePreview fileId={expense.attachment.id}>
              <PreviewTrigger type="button" aria-label={t('attachmentSelector.preview')}>
                <ExpenseClaimReceiptPreview
                  slug={slug}
                  claimId={claimId}
                  expenseId={expense.id}
                  mimeType={expense.attachment.type}
                />
              </PreviewTrigger>
            </FilePreview>
            <Caption>
              {[expense.merchant, formatEurosWithOpts(expense.amount ?? 0)]
                .filter(Boolean)
                .join(' · ')}
            </Caption>
            {expense.description && <Help>{expense.description}</Help>}
          </Tile>
        ))}
      </Grid>
    </Card>
  )
}

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const Tile = styled.div`
  width: ${({ theme }) => theme.layout.attachmentTileWidth}rem;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const PreviewTrigger = styled.button`
  position: relative;
  display: block;
  width: 100%;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
  }

  &:hover::after {
    background: ${({ theme }) => theme.colors.backdrop}22;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`

const Caption = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralBlack};
`
